<template>
  <tr :class="[isOpen ? 'expanded-row' : '', 'patient-examination-row']">
    <td>
      <div
        v-if="$can('prioritise_cases')"
        class="d-flex flex-wrap align-items-center"
      >
        <bookmark-icon
          v-if="examinationCollection.main_examination.isBookmarked"
          class="consultations-list-icon"
        /><flag-icon
          v-if="examinationCollection.main_examination.isFlagged"
          class="consultations-list-icon"
        /><priority-icon
          v-if="examinationCollection.main_examination.isPrioritized"
          class="consultations-list-icon"
        /><CdrFlagStatusBadge
          v-if="
            examinationCollection.main_examination.pendingCdrFlags?.length ||
            examinationCollection.hasPendingCdrFlag
          "
          class="consultations-list-icon"
        />
      </div>
      <div
        v-if="$can('mark_as_test_patient')"
        class="d-flex flex-wrap align-items-center"
      >
        <test-icon
          v-if="examinationCollection.main_examination.isTestPatient"
          class="consultations-list-icon"
        />
      </div>
    </td>

    <!-- TODO: decide on under_score or camelCase. It would be cleaner and easier to use under_score on all value -->
    <td style="word-break: break-all">
      {{ examinationCollection.main_examination.caseNo }}
    </td>
    <td>
      {{ location }}
      <span v-if="locationLaterality">{{ locationLaterality }}</span>
    </td>
    <td v-if="isExpanded">
      {{ examinationCollection.main_examination.createdAt }}
    </td>
    <td>
      {{ hasLastConsultationColumnHeader ? mostRecentConsultationDate : " " }}
    </td>
    <td v-if="isExpanded">
      {{ examinationCollection.main_examination.createdUser }}
    </td>
    <td>
      <case-status
        :hide-label="true"
        :examination="examinationCollection.main_examination"
      />
    </td>
    <td class="text-right">
      <action-handler
        v-if="actionsMenu"
        icon-only
        class="d-inline"
        :examination-actions="examinationActions"
        :product="productDefinition"
        :loading="actionsMenu.loading"
        :error-updating-actions="actionsMenu.errorUpdatingActions"
        :case-closed-at="caseClosedAt"
        :view-option="actionsMenu.viewOption"
        @on-action="onAction"
      />
      <div
        :class="[
          isOpen ? 'fa-angle-up' : 'fa-angle-down',
          'far',
          'd-inline',
          'pl-3',
          'pr-2',
        ]"
      ></div>
      <VueJsDialogMixinWrapper
        v-if="!!showPrintDialogWithOptions"
        :data-component="showPrintDialogComponent"
        :data-options="showPrintDialogWithOptions"
        @close="closePrintCase"
      />
      <CdrFlagDialog
        v-if="showCdrFlagDialog"
        :show="showCdrFlagDialog"
        :target-type="cdrScopeType.EXAMINATION"
        :target-id="examinationCollection.main_examination.id"
        :wound-acknowledgement="woundAcknowledgement"
        @close="showCdrFlagDialog = false"
        @created="showCdrFlagDialog = false"
      />
    </td>
  </tr>
</template>

<script>
import CaseStatus from "../../../Examinations/Examination/ExaminationComponents/components/CaseStatus.vue";
import BookmarkIcon from "../../../Utils/ActionIcons/BookmarkIcon.vue";
import FlagIcon from "../../../Utils/ActionIcons/FlagIcon.vue";
import PriorityIcon from "../../../Utils/ActionIcons/PriorityIcon.vue";
import TestIcon from "../../../Utils/ActionIcons/TestIcon.vue";
import { lang } from "../../../../i18n";
import ActionHandler from "../../../Examinations/Examination/ExaminationComponents/components/ActionHandler.vue";
import { examinationCloseConfirm } from "../../../Examinations/examination-close-confirm";
import { practitionerHttpService } from "../../../../app/practitioner-portal/store/practitioner-http-service";
import {
  PRODUCT_ACTION_NAMES,
  STORE_CONSTANTS,
} from "../../../../helpers/definitions";
import VueJsDialogMixinWrapper from "../../../../mixins/VueJsDialogMixinWrapper.vue";
import CustomPrintViewDialog from "../../../Examinations/Examination/ExaminationComponents/components/PrintCaseView.vue";
import CdrFlagDialog from "../../../ClinicalDataRemoval/CdrFlagDialog.vue";
import CdrFlagStatusBadge from "../../../ClinicalDataRemoval/CdrFlagStatusBadge.vue";
import { CDR_SCOPE_TYPE } from "../../../ClinicalDataRemoval/cdr-constants";
import { getWoundAcknowledgement } from "../../../ClinicalDataRemoval/wound-acknowledgement";

export default {
  components: {
    VueJsDialogMixinWrapper,
    ActionHandler,
    CdrFlagStatusBadge,
    CdrFlagDialog,
    CaseStatus,
    BookmarkIcon,
    FlagIcon,
    PriorityIcon,
    TestIcon,
  },
  props: {
    examinationCollection: {
      main_examination: Object,
    },
    productDefinition: Object,
    isExpanded: Boolean,
    isOpen: Boolean,
    hasLastConsultationColumnHeader: Boolean,
  },
  data() {
    return {
      location: undefined,
      showPrintDialogComponent: CustomPrintViewDialog,
      showPrintDialogWithOptions: null,
      showCdrFlagDialog: false,
      cdrScopeType: CDR_SCOPE_TYPE,
      examinationActions: {
        isPrioritized: false,
        isFlagged: false,
        isBookmarked: false,
        isTestPatient: false,
        hasPendingCdrFlag: false,
      },
      actionsMenu: {
        loading: false,
        examinationActions: this.examinationActions,
        errorUpdatingActions: null,
        caseClosedAt: null,
        viewOption: "patient_overview_header",
        onAction: null,
      },
    };
  },
  computed: {
    woundAcknowledgement() {
      return getWoundAcknowledgement(
        this.examinationCollection.main_examination,
      );
    },
    caseClosedAt() {
      return this.examinationCollection.main_examination.caseCloseAt ?? null;
    },
    locationLaterality() {
      const { main_examination } = this.examinationCollection;

      if ("case_data" in main_examination) {
        const { case_data } = main_examination;
        if (case_data.ulcer_location_laterality) {
          return `(${lang.get(`examination.${case_data.ulcer_location_laterality}`)})`;
        }
      }
    },
    mostRecentConsultationDate() {
      return (
        this.examinationCollection.examinations?.[0]?.photographedAt ?? "-"
      );
    },
  },
  mounted: function () {
    this.mapLocation();
    this.examinationActions.hasPendingCdrFlag =
      this.examinationCollection.main_examination.pendingCdrFlags?.some(
        (f) => f.isOwnFlag,
      ) ?? false;
  },
  methods: {
    mapLocation: function () {
      const { main_examination } = this.examinationCollection;
      if ("case_data" in main_examination) {
        const { case_data } = main_examination;

        let locationField;
        if ("lesion_location" in case_data)
          locationField = case_data.lesion_location;
        else if ("ulcer_location" in case_data)
          locationField = case_data.ulcer_location;
        else return;

        let result = "";
        if (
          typeof locationField === "string" ||
          locationField instanceof String
        ) {
          result = this.trans(`examination.${locationField}`);
        } else if (locationField) {
          Object.keys(locationField).forEach((location) => {
            const translated = this.trans(`examination.${location}`);
            if (locationField[location])
              result += result === "" ? translated : `/${translated}`;
          });
        }

        this.location = result;
      }
    },
    onAction: function (action) {
      if (action.submitable) this.updateCaseActions(action);
      else {
        switch (action.name) {
          case PRODUCT_ACTION_NAMES.PRINT_CASE:
            this.printCase();
            break;
          case PRODUCT_ACTION_NAMES.CLOSE_CASE:
            this.closeCase();
            break;
          case PRODUCT_ACTION_NAMES.CDR_REPORT_CASE:
            this.showCdrFlagDialog = true;
            break;
        }
      }
    },
    printCase: function () {
      const latestExam = this.examinationCollection.examinations?.[0];
      this.showPrintDialogWithOptions = {
        data: {
          uri: `/api/internal/examinations/${latestExam?.id ?? this.examinationCollection.main_examination.id}/print`,
          caseNo: this.examinationCollection.main_examination.caseNo,
          productDefinition: this.productDefinition,
          showPatientLevelReports: this.showPatientLevelReports,
        },
      };
    },
    closePrintCase: function () {
      this.showPrintDialogWithOptions = null;
    },
    closeCase: async function () {
      try {
        await examinationCloseConfirm(
          this.$vueAlert.confirm,
          this.examinationCollection.main_examination,
        );
        await practitionerHttpService.get(
          `/api/internal/tumour-wound/close/${this.examinationCollection.main_examination.id}`,
        );
        this.$store.dispatch(STORE_CONSTANTS.LOAD_PATIENT_CASES);
      } catch (e) {
        return;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.expanded-row {
  background-color: #f5f5f5;
  border: 2px solid #f5f5f5;

  td {
    border-bottom: 2px solid #dee2e6 !important;
  }

  &:hover {
    background-color: #f5f5f5;
  }
}

.text-right {
  white-space: nowrap;
}

.patient-examination-row {
  td:first-of-type {
    border-left: 2px solid #f5f5f5;
  }

  td:last-of-type {
    border-right: 2px solid #f5f5f5;
  }

  td {
    border-top: 2px solid #f5f5f5;
    border-bottom: 2px solid #f5f5f5;
    padding: 0.5rem;
    cursor: pointer;
  }

  .consultations-list-icon {
    padding: 0 2px;
  }
}

.far.fa-angle-down,
.far.fa-angle-up {
  font-size: large;
  vertical-align: middle;
  border-left: 1px solid #dadada;
}

.btn {
  min-width: 120px;
}

@media (max-width: 700px) {
  .btn {
    min-width: auto;
  }
}
</style>
